import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  FileText, 
  Upload, 
  BarChart3, 
  Settings, 
  Menu,
  X,
  ChevronRight,
  Presentation,
  ClipboardCheck,
  CalendarDays,
  GitCompare,
  Building2,
  FolderKanban,
  BookOpen,
  LogOut,
  ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", exact: true },
  { path: "/dashboard/data-status", icon: ClipboardCheck, label: "Data Status" },
  { path: "/dashboard/monthly-entry", icon: FileText, label: "Monthly Entry" },
  { path: "/dashboard/my-schedule", icon: CalendarDays, label: "My Schedule" },
  { path: "/dashboard/import", icon: Upload, label: "Data Import" },
  { path: "/dashboard/reports", icon: BarChart3, label: "Reports" },
  { path: "/dashboard/year-comparison", icon: GitCompare, label: "Year Analysis" },
  { path: "/dashboard/committee", icon: Presentation, label: "Committee View" },
  { divider: true, label: "Documents & Training" },
  { path: "/dashboard/projects", icon: FolderKanban, label: "Projects" },
  { path: "/dashboard/documents", icon: BookOpen, label: "Documents" },
  { divider: true, label: "Settings" },
  { path: "/dashboard/settings", icon: Settings, label: "Settings" },
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    // Get user and org from localStorage
    const storedUser = localStorage.getItem("user");
    const storedOrg = localStorage.getItem("organization");
    if (storedUser) setUser(JSON.parse(storedUser));
    if (storedOrg) setOrganization(JSON.parse(storedOrg));
  }, []);

  // Fetch logo on mount
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings/logo`, { credentials: "include" });
        const data = await res.json();
        if (data.exists && data.data) {
          setLogoUrl(`data:${data.content_type};base64,${data.data}`);
        }
      } catch (error) {
        console.error("Failed to fetch logo:", error);
      }
    };
    fetchLogo();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      
      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Clean Logo Header */}
        <div className="h-20 flex items-center justify-between px-4 bg-white border-b border-slate-200">
          <div className="flex items-center gap-3">
            {/* Organization Logo - uploaded via Settings or default icon */}
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Organization Logo" 
                className="h-12 w-auto max-w-[140px] object-contain"
                data-testid="sidebar-logo"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 3L4 7v6c0 5.5 3.5 10 8 11 4.5-1 8-5.5 8-11V7l-8-4z" />
                  <path d="M6 12h4l2-3 2 5 2-2h4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            )}
            {!logoUrl && (
              <div>
                <p className="font-semibold text-slate-800 text-sm">nikosafe</p>
                <p className="text-[10px] text-emerald-600">infectionprevention</p>
              </div>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-close-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* User Info */}
        <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100">
          <p className="text-xs text-slate-500">{user?.name || "User"}</p>
          <p className="text-sm font-semibold text-emerald-700">{organization?.name || "Organization"}</p>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {navItems.map((item, index) => {
            if (item.divider) {
              return (
                <div key={`divider-${index}`} className="pt-4 pb-2">
                  <p className="px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                    {item.label}
                  </p>
                </div>
              );
            }
            
            const isActive = item.exact 
              ? location.pathname === item.path 
              : location.pathname.startsWith(item.path);
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                  isActive 
                    ? "bg-[#f5f0fa] text-[#6A26B5] border-l-[3px] border-[#6A26B5] -ml-[3px]" 
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                )}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className={cn("w-4 h-4", isActive ? "text-[#6A26B5]" : "text-slate-400")} />
                <span>{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto text-[#8b4fcf]" />}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer with Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-100 bg-white">
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={async () => {
              try {
                await fetch(`${API_URL}/api/auth/logout`, { method: "POST", credentials: "include" });
              } catch (e) {}
              localStorage.removeItem("user");
              localStorage.removeItem("organization");
              toast.success("Logged out successfully");
              navigate("/");
            }}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="sidebar-toggle-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {navItems.find(item => 
                item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path)
              )?.label || 'Dashboard'}
            </h2>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-slate-700">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.role === "owner" ? "Owner" : "Staff"}</p>
            </div>
            <div className="h-9 w-9 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 flex items-center justify-center">
              <span className="text-white text-sm font-bold">{user?.name?.charAt(0) || "U"}</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
